"use client";

/**
 * Progressive-disclosure wrapper for the residence page's dense tables
 * and grids. Children are ALWAYS in the DOM (server-rendered, so
 * crawlers keep the full table text); visually the panel unfolds with
 * a smooth grid-rows transition instead of the native <details>'s
 * instant pop. Keyboard-accessible button with aria-expanded.
 *
 * `openFrom`: a breakpoint from which the panel is simply open and the
 * button is gone (the home page's market card folds on phones only).
 */
import { useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";

const OPEN_FROM = {
  sm: { button: "sm:hidden", panel: "sm:grid-rows-[1fr] sm:opacity-100" },
  md: { button: "md:hidden", panel: "md:grid-rows-[1fr] md:opacity-100" },
  lg: { button: "lg:hidden", panel: "lg:grid-rows-[1fr] lg:opacity-100" },
} as const;

export default function SectionExpander({
  show,
  hide,
  children,
  className = "",
  openFrom,
}: {
  /** Collapsed-state label ("Etrafli"). */
  show: string;
  /** Expanded-state label ("Gizlet"). */
  hide: string;
  children: ReactNode;
  className?: string;
  /** Always open (no button) from this breakpoint up. */
  openFrom?: keyof typeof OPEN_FROM;
}) {
  const [open, setOpen] = useState(false);
  const from = openFrom ? OPEN_FROM[openFrom] : null;
  return (
    <div className={className}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex w-fit cursor-pointer select-none items-center gap-1 rounded-lg text-sm font-semibold text-gray-900 underline decoration-gray-300 underline-offset-2 transition-colors hover:decoration-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${from?.button ?? ""}`}
      >
        {open ? hide : show}
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(.22,1,.36,1)] motion-reduce:transition-none ${
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        } ${from?.panel ?? ""}`}
      >
        <div className="overflow-hidden">
          <div className="pt-3">{children}</div>
        </div>
      </div>
    </div>
  );
}
